import { useCallback } from 'react';
import { useReactFlow } from 'reactflow';

import { ReviewFindingTarget } from 'api/journey-reviews/requests';
import { useOpenTrigger } from 'ui/actions/triggers/use-open-trigger';

import { resolveTargetNodeId } from './review-utils';
import { useReviewPanelStore } from './use-review-panel-store';

import { useNodes } from '../hooks/use-nodes';
import {
  FOCUS_NODE_DURATION,
  FOCUS_NODE_MAX_ZOOM,
  FOCUS_NODE_PADDING,
  showActionSidebar,
} from '../show-action-sidebar';

export const useFindingNavigation = () => {
  const { getValidNodes, getNode } = useNodes();
  const { fitView } = useReactFlow();
  const closePanel = useReviewPanelStore((store) => store.close);
  const openPanel = useReviewPanelStore((store) => store.open);
  const openTrigger = useOpenTrigger();

  return useCallback(
    (target: ReviewFindingTarget) => {
      const nodeRefs = getValidNodes().map((node) => ({
        id: node.id,
        type: node.type,
      }));
      const nodeId = resolveTargetNodeId(target, nodeRefs);
      if (!nodeId) {
        return;
      }

      const node = getNode(nodeId);
      if (!node) {
        return;
      }

      fitView({
        nodes: [{ id: nodeId }],
        duration: FOCUS_NODE_DURATION,
        maxZoom: FOCUS_NODE_MAX_ZOOM,
        padding: FOCUS_NODE_PADDING,
      });
      // Both own the right-hand side, so hand the space over and take it back on
      // close — but only if something takes it. Triggers and branch bases have no
      // registered sidebar, so onHide would never fire and the panel would stay shut.
      const opened =
        target.kind === 'trigger' && target.id
          ? openTrigger(target.id, { onHide: openPanel })
          : showActionSidebar(node, { focusNode: false, onHide: openPanel });
      if (opened) {
        closePanel();
      }
    },
    [getValidNodes, getNode, fitView, closePanel, openPanel, openTrigger],
  );
};
